import React, { useState, useEffect, useCallback } from "react";
import { Plus, AlertCircle, Search } from "lucide-react";
import { toast } from "react-toastify";
import { purchaseAPI, batchAPI } from "../../services";
import BatchForm from "./BatchForm";
import StockForm from "./StockForm";

import "../../style/productDetail.css";
import "../../style/batches.css";

import EditIcon from "../../assets/icons/EditIcon.js";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";

import BackIcon from "../../assets/back-icon.svg";
import PushIcon from "../../assets/icons/PushIcon";
import NoStock from "../../assets/no-stock.svg";

const BatchDetailView = ({
  batch,
  onBack,
  onEditBatch,
  onAddStock,
  onEditStock,
  onDeleteStock,
  products = [],
  suppliers = [],
  allBatches = [],
}) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showStockForm, setShowStockForm] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [batchDetail, setBatchDetail] = useState(null);
  const [loadingBatchDetail, setLoadingBatchDetail] = useState(false);
  const [displayedItems, setDisplayedItems] = useState(30);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  // Helper function to format field names for better display
  const formatFieldName = (fieldName) => {
    const fieldMap = {
      unique_id: "Unique ID",
      product_id: "Product",
      batch_id: "Batch",
      buying_price: "Buying Price",
      selling_price: "Selling Price",
      record_point: "Record Point",
      available_qty: "Available Quantity",
    };

    return (
      fieldMap[fieldName] ||
      fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  const handleEditBatchSave = (batchData) => {
    onEditBatch(batchData);
    setShowEditForm(false);
  };

  // Convert API data to form format for editing
  const getEditableBatch = () => {
    if (!batch) return null;
    const editableBatch = {
      ...batch,
      invoiceNumber: batch.invoice_number,
      totalQuantity: batch.quantity,
      supplierId: batch.vendor,
      description: batch.description || "",
    };
    return editableBatch;
  };

  const handleAddStock = () => {
    setEditingStock(null);
    setShowStockForm(true);
  };

  const handleEditStock = (stock) => {
    setEditingStock(stock);
    setShowStockForm(true);
  };

  const handleStockFormSubmit = async (stockData) => {
    try {
      if (editingStock) {
        // Update existing purchase
        await handleEditStockAPI(editingStock.id, stockData);
        toast.success("Stock item updated successfully!");
      } else {
        // Create new purchase
        await handleAddStockAPI(batch.id, stockData);
        toast.success("Stock item added successfully!");
      }
      // Refresh batch details after operation
      await fetchBatchDetail();
      setShowStockForm(false);
      setEditingStock(null);
    } catch (error) {
      console.error("Error submitting stock form:", error);
      // Handle validation errors from backend
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for the specific error format: { status, message, errors }
        if (errorData.errors && typeof errorData.errors === "object") {
          let hasShownError = false;
          Object.keys(errorData.errors).forEach((field) => {
            const fieldErrors = errorData.errors[field];
            const formattedFieldName = formatFieldName(field);
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errorMsg) => {
                toast.error(`${formattedFieldName}: ${errorMsg}`);
                hasShownError = true;
              });
            } else if (typeof fieldErrors === "string") {
              toast.error(`${formattedFieldName}: ${fieldErrors}`);
              hasShownError = true;
            }
          });

          if (!hasShownError && errorData.message) {
            toast.error(errorData.message);
          }
        }
        // Check if it's a validation error with field-specific messages (alternative format)
        else if (
          typeof errorData === "object" &&
          !Array.isArray(errorData) &&
          !errorData.status
        ) {
          let hasShownError = false;
          Object.keys(errorData).forEach((field) => {
            const fieldErrors = errorData[field];
            const formattedFieldName = formatFieldName(field);
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errorMsg) => {
                toast.error(`${formattedFieldName}: ${errorMsg}`);
                hasShownError = true;
              });
            } else if (typeof fieldErrors === "string") {
              toast.error(`${formattedFieldName}: ${fieldErrors}`);
              hasShownError = true;
            }
          });

          if (!hasShownError) {
            toast.error(
              editingStock
                ? "Failed to update stock item"
                : "Failed to add stock item"
            );
          }
        } else if (typeof errorData === "string") {
          toast.error(errorData);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error(
            editingStock
              ? "Failed to update stock item"
              : "Failed to add stock item"
          );
        }
      } else {
        toast.error(
          editingStock
            ? "Failed to update stock item"
            : "Failed to add stock item"
        );
      }
    }
  };

  // Fetch batch details from API
  const fetchBatchDetail = useCallback(
    async (search = "") => {
      if (!batch?.id) return;

      setLoadingBatchDetail(true);
      setSearchLoading(true);
      try {
        if (search) {
          // Use purchase API for search
          const response = await purchaseAPI.getPurchasesList({
            batch_id: batch.id,
            search: search,
            page_size: 1000,
          });
          setStockItems(response.results || []);
          // Don't update batchDetail when searching
        } else {
          // Use batch detail API for full data
          const response = await batchAPI.getBatchDetail(batch.id);
          if (response && response.data) {
            setBatchDetail(response.data);
            setStockItems(response.data.products_list || []);
          }
        }
      } catch (error) {
        console.error("Error fetching batch detail:", error);
        if (!search) {
          setBatchDetail(null);
        }
        setStockItems([]);
      } finally {
        setLoadingBatchDetail(false);
        setSearchLoading(false);
      }
    },
    [batch?.id]
  );

  // Load batch details when batch changes
  useEffect(() => {
    fetchBatchDetail(searchQuery);
  }, [fetchBatchDetail, searchQuery]);

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop <= clientHeight + 50 &&
        !loadingStocks &&
        hasMore
      ) {
        setLoadingStocks(true);
        setTimeout(() => {
          setDisplayedItems((prev) => {
            const newCount = prev + 30;
            setHasMore(newCount < stockItems.length);
            return newCount;
          });
          setLoadingStocks(false);
        }, 500);
      }
    },
    [loadingStocks, hasMore, stockItems.length]
  );

  // Reset displayed items when stock items change
  useEffect(() => {
    setDisplayedItems(30);
    setHasMore(stockItems.length > 30);
  }, [stockItems.length]);

  // Add stock using purchase API
  const handleAddStockAPI = async (batchId, stockData) => {
    try {
      // Check if uniqueId exists and is not empty
      const uniqueIdValue = stockData.uniqueId;

      const purchasePayload = {
        product_id: stockData.productId,
        batch_id: batchId,
        quantity: stockData.quantity,
        selling_price: stockData.sellingPrice,
        buying_price: stockData.buyingPrice,
        record_point: 10, // Default record point
        available_qty: stockData.quantity,
      };

      // Only include unique_id if it exists and is not empty
      if (uniqueIdValue && uniqueIdValue.trim() !== "") {
        purchasePayload.unique_id = uniqueIdValue;
      }

      await purchaseAPI.createPurchase(purchasePayload);
    } catch (error) {
      console.error("Error adding stock:", error);
      throw error;
    }
  };

  // Edit stock using purchase API
  const handleEditStockAPI = async (purchaseId, stockData) => {
    try {
      const updatePayload = {
        selling_price: stockData.sellingPrice,
        buying_price: stockData.buyingPrice
      };
      
      if (stockData.uniqueId && stockData.uniqueId.trim() !== "") {
        updatePayload.unique_id = stockData.uniqueId.trim();
      }

      await purchaseAPI.updatePurchase(purchaseId, updatePayload);
    } catch (error) {
      console.error("Error updating stock:", error);
      throw error;
    }
  };

  // Delete stock using purchase API
  const handleDeleteStockAPI = async (purchaseId) => {
    try {
      await purchaseAPI.deletePurchase(purchaseId);

      toast.success("Stock item deleted successfully!");

      // Refresh batch details after deletion
      await fetchBatchDetail();
    } catch (error) {
      console.error("Error deleting stock:", error);

      // Handle error response
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for the specific error format: { status, message, errors }
        if (errorData.errors && typeof errorData.errors === "object") {
          let hasShownError = false;
          Object.keys(errorData.errors).forEach((field) => {
            const fieldErrors = errorData.errors[field];
            const formattedFieldName = formatFieldName(field);
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errorMsg) => {
                toast.error(`${formattedFieldName}: ${errorMsg}`);
                hasShownError = true;
              });
            } else if (typeof fieldErrors === "string") {
              toast.error(`${formattedFieldName}: ${fieldErrors}`);
              hasShownError = true;
            }
          });

          if (!hasShownError && errorData.message) {
            toast.error(errorData.message);
          }
        } else if (typeof errorData === "string") {
          toast.error(errorData);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error("Failed to delete stock item");
        }
      } else {
        toast.error("Failed to delete stock item");
      }

      throw error;
    }
  };

  const getSupplierName = () => {
    // Use vendor_details from API response if available
    if (batchDetail?.vendor_details) {
      return batchDetail.vendor_details.name;
    }
    if (batch?.vendor_details) {
      return batch.vendor_details.name;
    }
    // Fallback to suppliers array lookup
    const vendorId = batchDetail?.vendor || batch?.vendor;
    const supplier = suppliers.find((s) => s.id === vendorId);
    return supplier ? supplier.name : "Unknown Supplier";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!batch) {
    return (
      <div className="batches-detail-page">
        <div className="batches-detail-container">
          <div className="back-navigation">
            <button className="back-button" onClick={onBack}>
              <img src={BackIcon} alt="Back" />
              Back to Batches
            </button>
          </div>
          <div className="no-data-container">
            <div className="no-data-icon">
              <AlertCircle size={48} />
            </div>
            <div className="no-data-text">Batch not found</div>
          </div>
        </div>
      </div>
    );
  }

  const stocks = stockItems || [];
  const displayedStocks = stocks.slice(0, displayedItems);
  const usedQuantity = stocks.reduce(
    (sum, stock) => sum + (stock.available_qty || stock.quantity || 0),
    0
  );

  // Calculate total amount from stock items or use batchDetail data
  const totalAmountFromStocks =
    batchDetail?.total_amount ||
    stocks.reduce((sum, stock) => sum + (stock.total_amount || 0), 0);

  // Use batchDetail data if available, otherwise fall back to batch data
  const displayBatch = batchDetail || batch;

  return (
    <div className="batches-detail-page">
      <div className="batches-detail-container">
        <div className="back-navigation">
          <button className="back-button" onClick={onBack}>
            <img src={BackIcon} alt="Back" />
            Back to Batches
          </button>
        </div>

        <div className="batches-detail-content">
          <div className="page-header">
            <div className="page-header-content">
              <div className="page-title-section">
                <h1 className="page-title">Batch Details</h1>
                <p className="page-subtitle">
                  Complete information about batch{" "}
                  {displayBatch.invoice_number || displayBatch.id}
                </p>
              </div>
              <div className="page-actions">
                <button className="btn btn-outline-primary batch-push-btn">
                  <PushIcon />
                  Push
                </button>
                <button
                  className="btn btn-primary batch-add-stock-btn"
                  onClick={handleAddStock}
                >
                  <Plus size={16} />
                  Add Stock
                </button>
              </div>
            </div>
          </div>

          {/* Batch Summary Card */}
          <div className="batch-summary-card">
            <h3 className="batch-summary-title">Batch Summary</h3>
            <div className="batch-summary-grid">
              <div className="batch-summary-item">
                <div className="batch-summary-label">Batch No.</div>
                <div className="batch-summary-value">
                  {displayBatch.batch_number}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Supplier</div>
                <div className="batch-summary-value">{getSupplierName()}</div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Invoice No</div>
                <div className="batch-summary-value">
                  {displayBatch.invoice_number || "-"}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Description</div>
                <div className="batch-summary-value">
                  {displayBatch.description || "No description"}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Date</div>
                <div className="batch-summary-value">
                  {formatDate(displayBatch.created_at)}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Total Amount</div>
                <div className="batch-summary-value batch-amount-value">
                  €{totalAmountFromStocks.toFixed(2)}/{displayBatch.amount}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Total Quantity</div>
                <div className="batch-summary-value">
                  {displayBatch.total_qty || usedQuantity}/
                  {displayBatch.quantity}
                </div>
              </div>
            </div>
          </div>

          {/* Stock Items Card */}
          <div className="stock-items-card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 className="stock-items-title" style={{ margin: 0 }}>
                Stock Items
              </h3>

              {/* SearchBar */}
              <div
                className="d-flex align-items-center gap-3"
                // style={{ width: "548px" }}
              >
                <div className="position-relative flex-grow-1">
                  <input
                    type="text"
                    className="form-control mrph-form input-form ps-5"
                    placeholder="Search stock items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Search
                    size={16}
                    className="position-absolute top-50 start-0 translate-middle-y ms-3"
                    style={{ color: "#8C959F" }}
                  />
                </div>
              </div>
            </div>

            {(loadingBatchDetail && !searchLoading) || loadingStocks ? (
              <div className="stock-items-loading">
                Loading batch details...
              </div>
            ) : stocks.length === 0 ? (
              <div className="stock-items-empty">
                <div className="stock-items-empty-icon">
                  <img src={NoStock} alt="No Stock" />
                </div>
                <div className="stock-items-empty-text">
                  No stock items added to this batch yet
                </div>
              </div>
            ) : (
              <div
                className="stock-items-table-container"
                onScroll={handleScroll}
              >
                <table className="stock-items-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>PRODUCT</th>
                      <th>CATEGORY</th>
                      <th>BUYING PRICE</th>
                      <th>SELLING PRICE</th>
                      <th>QTY.</th>
                      <th>DATE</th>
                      <th>TOTAL AMOUNT</th>
                      <th style={{ textAlign: "center" }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedStocks.map((stock, index) => {
                      // Handle the new API response structure from products_list
                      const buyingPrice = stock.buying_price || 0;
                      const sellingPrice = stock.selling_price || 0;
                      const quantity = stock.initial_quantity || 0;
                      const createdAt = stock.created_at;
                      const totalAmount =
                        stock.total_amount || buyingPrice * quantity;
                      const productName =
                        stock.product_name || "Unknown Product";
                      const categoryName = stock.category_name || "Mobile";
                      // const uniqueId = stock.unique_id || "";

                      const formatDate = (dateString) => {
                        if (!dateString) return "N/A";
                        return new Date(dateString).toLocaleDateString(
                          "en-US",
                          {
                            month: "numeric",
                            day: "numeric",
                            year: "numeric",
                          }
                        );
                      };

                      return (
                        <tr key={stock.id || index}>
                          <td>{index + 1}</td>
                          <td className="product-name-cell">
                            <div
                              className="product-name-truncate"
                              title={productName}
                            >
                              {productName}
                            </div>
                            <div
                              className="product-details-truncate"
                              title={[
                                stock.product_detail?.ram,
                                stock.product_detail?.storage,
                                stock.product_detail?.variants
                                  ?.map((v) => Object.values(v).join(", "))
                                  .join(", "),
                                stock.product_detail?.suffix,
                                stock.product_detail?.used_mobile !==
                                  undefined &&
                                stock.product_detail?.used_mobile !== null
                                  ? stock.product_detail.used_mobile === false
                                    ? "New"
                                    : "Used"
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" | ")}
                            >
                              {stock.product_detail?.ram &&
                                `${stock.product_detail.ram}`}
                              {stock.product_detail?.ram &&
                                stock.product_detail?.storage &&
                                " | "}
                              {stock.product_detail?.storage &&
                                `${stock.product_detail.storage}`}
                              {stock.product_detail?.variants &&
                                stock.product_detail.variants.length > 0 && (
                                  <>
                                    {(stock.product_detail?.ram ||
                                      stock.product_detail?.storage) &&
                                      " | "}
                                    {stock.product_detail.variants
                                      .map((variant) =>
                                        Object.values(variant).join(", ")
                                      )
                                      .join(", ")}
                                  </>
                                )}
                              {stock.product_detail?.suffix && (
                                <>
                                  {(stock.product_detail?.ram ||
                                    stock.product_detail?.storage ||
                                    (stock.product_detail?.variants &&
                                      stock.product_detail.variants.length >
                                        0)) &&
                                    " | "}
                                  {stock.product_detail.suffix}
                                </>
                              )}
                              {stock.product_detail?.used_mobile !==
                                undefined &&
                                stock.product_detail?.used_mobile !== null && (
                                  <>
                                    {(stock.product_detail?.ram ||
                                      stock.product_detail?.storage ||
                                      (stock.product_detail?.variants &&
                                        stock.product_detail.variants.length >
                                          0) ||
                                      stock.product_detail?.suffix) &&
                                      " | "}
                                    {stock.product_detail.used_mobile === false
                                      ? "New"
                                      : "Used"}
                                  </>
                                )}
                            </div>
                          </td>
                          <td className="category-cell">{categoryName}</td>
                          <td className="price-cell">
                            €{buyingPrice.toFixed(2)}
                          </td>
                          <td className="price-cell">
                            €{sellingPrice.toFixed(2)}
                          </td>
                          <td className="quantity-cell">{quantity}</td>
                          <td className="date-cell">{formatDate(createdAt)}</td>
                          <td className="total-amount-cell">
                            €{totalAmount.toFixed(2)}
                          </td>
                          <td
                            className="action-cell"
                            style={{ borderBottom: "1px solid #f0f2f5" }}
                          >
                            <div className="action-buttons">
                              <button
                                className="action-btn view-btn"
                                onClick={() => handleEditStock(stock)}
                                title="Edit"
                              >
                                <EditIcon />
                              </button>
                              <button
                                className="action-btn delete"
                                onClick={() => handleDeleteStockAPI(stock.id)}
                                title="Delete"
                              >
                                <RedDeleteIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {loadingStocks && (
                      <>
                        {[...Array(5)].map((_, i) => (
                          <tr key={`skeleton-${i}`} className="skeleton-row">
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditForm && (
        <BatchForm
          onSubmit={handleEditBatchSave}
          onCancel={() => setShowEditForm(false)}
          editingBatch={getEditableBatch()}
          suppliers={suppliers}
        />
      )}

      {showStockForm && (
        <StockForm
          onSubmit={handleStockFormSubmit}
          onCancel={() => {
            setShowStockForm(false);
            setEditingStock(null);
          }}
          editingStock={editingStock}
          products={products}
          suppliers={suppliers}
          allBatches={allBatches}
        />
      )}
    </div>
  );
};

export default BatchDetailView;
